import { Grow } from '@mui/material'
import {
  Delete,
  Edit,
  Assignment,
  Build,
  HomeRepairService,
} from '@mui/icons-material'
import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate, useLocation } from 'react-router-dom'
import { I18n, Translate } from 'common'
import { AutoCheckImage } from '../../../ui-components/AutoCheckImage'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import { useSpecificConditionStore } from '../../../store/specificConditions/SpecificConditionsStore'
import { useUserStore } from '../../../store/user/UserStore'
import { UserAttributeKey } from '../../../store/user/interfaces'
import {
  PartsCatalogType,
  usePartsCatalog,
} from '../../../store/partsCatalog/PartsCatalog'
import { breakpoints } from '../../../theme/breakpoints'
import styled from 'styled-components'
import { defaultTheme } from '../../../theme/index'
import LaborLookupDrawer from '../../labor/lookup/LookupDrawer'
import {
  useVehicleWidgetStore,
  VehicleWidgetDisplayState,
} from './store/VehicleWidgetStore'
import { StoreInstances } from '../../../store/StoreInstancesContainer'
import LaborResultDrawer from '../../labor/results/LaborResultDrawer'
import { ServiceIntervalsDrawer } from './components/ServiceIntervalsDrawer'
import { Button } from '../../../ui-components/forms/Button'
import { useSearchStore } from '../../../store/search/SearchStore'
import { show } from '@ebay/nice-modal-react'
import { VehicleSpecificationDrawer } from './components/VehicleSpecificationDrawer'
import { VinDetailsDrawer } from '../../vehicleHistory/components/VinDetailsDrawer'
import { TrackingManager } from '../../../config/analytics/tracking'
import { formatVehicleDisplayWithOutEngine } from 'src/helpers/utils'
import { SpecificConditionsModal } from 'src/features/specificConditions/components/SpecificConditionsModal'

interface SelectedVehicleProps {
  onEditVehicle: () => void
}

export const SelectedVehicle = observer(
  (props: SelectedVehicleProps): ReactElement => {
    const location = useLocation()
    const navigate = useNavigate()
    const searchStore = useSearchStore()
    const enableGrowTransition =
      searchStore.showVehicleTransition &&
      location.pathname !== '/home' &&
      location.pathname !== '/searchResults'

    const { onEditVehicle } = props
    const { currentVehicle } = searchStore
    const { terminologies } = usePartsCatalog()
    const {
      fetchSpecificConditionQuestions,
      specificConditionsLoading,
      setSpecificConditionsLoading,
    } = useSpecificConditionStore()

    const vehicleWidgetStore = useVehicleWidgetStore()

    const {
      setShowLaborResultModal,
      showLaborResultModal,
      showServiceIntervalsDrawer,
      setShowServiceIntervalsDrawer,
      isSpecificationsEnabled,
      isServiceIntervalsEnabled,
      isAutoCheckEnabled,
    } = useUiStateStore()

    const userStore = useUserStore()
    const showLaborLookup =
      userStore.getUserAttribute(UserAttributeKey.epeFeatures_labor) === 'true'

    const goToSearchResults = (): void => {
      searchStore.searchByNavigation()
      navigate('/searchResults')
    }

    const handleEditFitmentConditions = async () => {
      try {
        setSpecificConditionsLoading(true)
        const formattedVehicle = {
          ...searchStore.currentVehicle,
          initialized: undefined,
        }
        const specificConditionQuestions =
          await fetchSpecificConditionQuestions(
            formattedVehicle,
            terminologies,
            PartsCatalogType.PARTS_SEARCH
          )
        if (specificConditionQuestions.length > 0) {
          show(SpecificConditionsModal, {
            proceed: goToSearchResults,
          })
        }
      } finally {
        setSpecificConditionsLoading(false)
      }
    }

    const handleDeleteVehicle = () => {
      if (
        location.pathname === '/partsCatalog' ||
        location.pathname === '/searchResults'
      )
        navigate('home')
      searchStore.deleteCurrentVehicle()
      vehicleWidgetStore.setIsDrawerOpened(false)
      vehicleWidgetStore.setDisplayState(VehicleWidgetDisplayState.select)
      StoreInstances.uiStore.displaySuccessNotification(
        'vehicleSuccessfullyDeleted'
      )
      TrackingManager.clearVehicle()
    }

    const handleChangeVehicle = () => {
      vehicleWidgetStore.setDisplayState(VehicleWidgetDisplayState.edit)
    }

    const handleLaborLookupClick = () => {
      show(LaborLookupDrawer, {
        vehicle: currentVehicle,
        setLaborResult: setShowLaborResultModal,
      })
    }

    const handleShowSeviceIntervals = () => {
      setShowServiceIntervalsDrawer(true)
    }
    const showEditFitmentConditions =
      (searchStore?.currentVehicle?.specificConditions?.length || 0) > 0
    const subModel = currentVehicle.vehicleSpecs?.find(
      (obj) => obj.label === 'Series'
    )?.value

    // other motives changes
    const disableDetailsInfo = currentVehicle?.type?.id !== 111

    return (
      <Grow in appear={enableGrowTransition} timeout={1000}>
        <Wrapper>
          <VehicleInfo>
            <LeftSide>
              <MainRow>
                <LeftContainer>
                  <FlexColumn>
                    <Title>
                      {formatVehicleDisplayWithOutEngine(currentVehicle)}
                    </Title>
                    <SubTitle>
                      {StoreInstances.userStore.isCclEnabled &&
                        subModel?.length > 0 &&
                        subModel && (
                          <span>
                            <VehicleDetailLabel>
                              <I18n name="subModel" />:
                            </VehicleDetailLabel>
                            <VehicleDetail>{subModel}</VehicleDetail>
                          </span>
                        )}

                      {currentVehicle.engine?.value && (
                        <span>
                          <VehicleDetailLabel>
                            <I18n name="engine" />:
                          </VehicleDetailLabel>
                          <VehicleDetail>
                            {currentVehicle.engine.value}
                          </VehicleDetail>
                        </span>
                      )}
                      {currentVehicle.vin && (
                        <span>
                          <VehicleDetailLabel>
                            <I18n name="vin" />:
                          </VehicleDetailLabel>
                          <VehicleDetailLink
                            onClick={() =>
                              show(VinDetailsDrawer, {
                                VIN: currentVehicle?.vin,
                              })
                            }
                            type="button"
                          >
                            {currentVehicle.vin}
                          </VehicleDetailLink>
                        </span>
                      )}
                      {currentVehicle.plate && (
                        <span>
                          <VehicleDetailLabel>
                            <I18n name="license" />:
                          </VehicleDetailLabel>
                          <VehicleDetail>
                            {currentVehicle.state}, {currentVehicle.plate}
                          </VehicleDetail>
                        </span>
                      )}
                    </SubTitle>

                    {import.meta.env.VITE_FEATURE_VEHICLE_LINKS_ENABLED ===
                      'true' &&
                      !disableDetailsInfo && (
                        <DetailsInfo>
                          {isSpecificationsEnabled() && (
                            <DetailsInfoAction
                              onClick={() => {
                                show(VehicleSpecificationDrawer, {
                                  storeGroupsSelected: [],
                                })
                              }}
                            >
                              <Assignment
                                fontSize="inherit"
                                style={{ margin: 'auto 0px' }}
                              />
                              <I18n name="specifications" />
                            </DetailsInfoAction>
                          )}
                          {isServiceIntervalsEnabled() && (
                            <DetailsInfoAction
                              onClick={handleShowSeviceIntervals}
                            >
                              <Build
                                fontSize="inherit"
                                style={{ margin: 'auto 0px' }}
                              />
                              <I18n name="serviceIntervals" />
                            </DetailsInfoAction>
                          )}
                          {showLaborLookup && (
                            <LaborLookupLink onClick={handleLaborLookupClick}>
                              <HomeRepairService
                                fontSize="inherit"
                                style={{ margin: 'auto 0px' }}
                              />
                              <I18n name="laborLookup" />
                            </LaborLookupLink>
                          )}
                          {showEditFitmentConditions &&
                            location.pathname === `/searchResults` && (
                              <EditFitmentConditions
                                onClick={() => {
                                  handleEditFitmentConditions()
                                }}
                              >
                                {specificConditionsLoading ? (
                                  <StyledSpinner animation="border" size="sm" />
                                ) : (
                                  <StyledEditIcon />
                                )}
                                {Translate('editFitmentConditions')}
                              </EditFitmentConditions>
                            )}
                        </DetailsInfo>
                      )}

                    {isAutoCheckEnabled() && (
                      <MobileAutoCheck>
                        <AutoCheckImage vin={currentVehicle.vin} size="small" />
                      </MobileAutoCheck>
                    )}
                  </FlexColumn>
                </LeftContainer>

                <RightContainer>
                  <ButtonRow>
                    <EditButton onClick={onEditVehicle}>
                      <EditIcon>
                        <Edit style={{ marginRight: 4, fontSize: '18px' }} />
                      </EditIcon>
                      <I18n name="changeVehicle" />
                    </EditButton>
                    <EditButton onClick={handleDeleteVehicle}>
                      <DeleteIcon>
                        <Delete style={{ marginRight: 4, fontSize: '18px' }} />
                      </DeleteIcon>
                      <I18n name="clearVehicle" />
                    </EditButton>
                  </ButtonRow>
                  {isAutoCheckEnabled() && (
                    <AutoCheckImage vin={currentVehicle.vin} size="medium" />
                  )}
                </RightContainer>
              </MainRow>
            </LeftSide>

            <MobileActionButtons>
              <MobileDeleteButton>
                <Button
                  style={{ width: '100%' }}
                  variant="secondary"
                  onClick={handleChangeVehicle}
                  text="changeVehicle"
                  icon={
                    <Edit
                      style={{ fontSize: '18px', margin: '-4px 4px 0px 0px' }}
                    />
                  }
                />
              </MobileDeleteButton>
              <MobileEditButton>
                <Button
                  style={{ width: '100%' }}
                  text="clearVehicle"
                  variant="secondary"
                  onClick={handleDeleteVehicle}
                  icon={
                    <Delete
                      style={{ fontSize: '18px', margin: '-4px 4px 0px 0px' }}
                    />
                  }
                />
              </MobileEditButton>
            </MobileActionButtons>
          </VehicleInfo>
          <LaborResultDrawer
            open={showLaborResultModal}
            setOpen={setShowLaborResultModal}
          />
          {showServiceIntervalsDrawer && (
            <ServiceIntervalsDrawer
              open={showServiceIntervalsDrawer}
              onClose={() => {
                setShowServiceIntervalsDrawer(false)
              }}
              showEditFitmentConditions={showEditFitmentConditions}
              handleEditFitmentConditions={handleEditFitmentConditions}
            />
          )}
        </Wrapper>
      </Grow>
    )
  }
)

const Wrapper = styled.div`
  flex-grow: 1;
  @media screen and (width <= ${breakpoints.tabletMedium}px) {
    flex-direction: column;
    align-items: baseline;
  }
`

const ButtonRow = styled.div`
  display: flex;
  gap: 32px;
`

const MainRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media screen and (width < ${breakpoints.tabletMedium}px) {
    flex-direction: column;
  }
`

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-end;

  @media screen and (width < ${breakpoints.tabletMedium}px) {
    display: none;
  }
`

const MobileAutoCheck = styled.div`
  display: none;
  margin-bottom: 15px;

  @media screen and (width < ${breakpoints.tabletMedium}px) {
    display: block;
  }
`

const LeftSide = styled.div`
  @media screen and (width < ${breakpoints.tabletMedium}px) {
    flex-direction: column;
    align-items: baseline;
  }
`

const DetailsInfoAction = styled.span`
  cursor: pointer;
  display: flex;
  gap: 4px;
  margin: auto 0px;
  text-wrap: nowrap;
`

const LaborLookupLink = styled(DetailsInfoAction)`
  text-wrap: nowrap;
`

const DetailsInfo = styled.div`
  display: flex;
  gap: 24px;
  color: ${defaultTheme.colors.link};
  font-family: 'Roboto', serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  height: 24px;

  @media screen and (${breakpoints.mobileMedium}px < width < ${breakpoints.tabletMedium}px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    height: fit-content;
    margin: 0px 0px 16px 0px;
  }

  @media screen and (width <= ${breakpoints.mobileMedium}px) {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 20px;
    height: fit-content;
    margin: 20px 0px;
  }
`

const MobileActionButtons = styled.div`
  display: none;
  gap: 8px;
  width: 100%;
  @media screen and (width < ${breakpoints.tabletMedium}px) {
    display: flex;
  }

  @media screen and (width <= ${breakpoints.mobileMedium + 25}px) {
    flex-direction: column;
  }
`

const MobileEditButton = styled.div`
  width: 100%;
`
const MobileDeleteButton = styled.div`
  width: 100%;
`

const VehicleInfo = styled.div`
  width: 100%;
`

const Title = styled.span`
  display: flex;
  font-family: Oswald, serif;
  font-weight: bold;
  font-size: 24px;

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    font-size: 18px;
  }
`

const SubTitle = styled.span`
  display: flex;
  gap: 16px;
  margin: 8px 0px 16px 0px;
  flex-wrap: wrap;

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    margin: 4px 0px 12px 0px;
  }

  @media screen and (width <= ${breakpoints.mobileXLarge}px) {
    flex-direction: column;
  }
`

const VehicleDetailLabel = styled.span`
  align-self: flex-end;
  font-family: Roboto, serif;
  font-size: 16px;
  font-weight: bold;
  margin-right: 4px;

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    font-size: 14px;
  }
`

const VehicleDetail = styled.span`
  align-self: flex-end;
  font-family: Roboto, serif;
  font-size: 16px;
  font-weight: normal;

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    font-size: 14px;
  }
`
const VehicleDetailLink = styled.button`
  align-self: flex-end;
  font-family: Roboto, serif;
  font-size: 16px;
  font-weight: normal;
  background: none;
  border: none;
  color: ${defaultTheme.colors.blueLight};

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    font-size: 14px;
  }
`

const EditButton = styled.div`
  display: flex;
  align-items: center;
  font-family: Oswald, serif;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
  @media screen and (max-width: ${breakpoints.tabletMedium}px) {
    width: 100;
  }
`

const EditFitmentConditions = styled.div`
  align-self: flex-start;
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 23px;
  color: ${defaultTheme.colors.blueLight};
  cursor: pointer;
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`
const EditIcon = styled.div``
const DeleteIcon = styled.div``

const StyledEditIcon = styled(Edit)`
  margin-top: -5px;
  padding-right: 5px;
`
const StyledSpinner = styled(Spinner)`
  margin-right: 5px;
`
